import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { recordRetainedBeaconEligibilityDegradation, retainedBeaconDischargeEligibility } from '@/graph/patients';

export const RETAINED_BEACON_ELIGIBILITY_TIMEOUT_MS = 5000;

export type RetainedBeaconChoice = 'standard' | 'retain';
export type RetainedBeaconEligibilityStatus = 'idle' | 'loading' | 'ineligible' | 'eligible';

export interface RetainedBeaconEligibility {
  eligible: boolean;
  reason: string;
  procedureId?: number | null;
  scheduledStart?: string | null;
  orId?: number | null;
  expiresAt?: string | null;
}

export function canSubmitRetainedBeaconDischarge(
  status: RetainedBeaconEligibilityStatus,
  choice: RetainedBeaconChoice | null
) {
  return status !== 'eligible' || choice !== null;
}

export function shouldKeepBeacon(status: RetainedBeaconEligibilityStatus, choice: RetainedBeaconChoice | null) {
  return status === 'eligible' && choice === 'retain';
}

function elapsedSince(startedAt: number | null) {
  return startedAt == null ? undefined : Date.now() - startedAt;
}

interface Options {
  patientId?: number | string | null;
  enabled: boolean;
  timeoutMs?: number;
  surface?: string;
}

class EligibilityTimeoutError extends Error {}

export function useRetainedBeaconDischarge({
  patientId,
  enabled,
  timeoutMs = RETAINED_BEACON_ELIGIBILITY_TIMEOUT_MS,
  surface = 'unknown',
}: Options) {
  const client = useApolloClient();
  const requestRef = useRef(0);
  const startedAtRef = useRef<number | null>(null);
  const [status, setStatus] = useState<RetainedBeaconEligibilityStatus>('idle');
  const [eligibility, setEligibility] = useState<RetainedBeaconEligibility | null>(null);
  const [choice, setChoice] = useState<RetainedBeaconChoice | null>(null);
  const [degradationReason, setDegradationReason] = useState<'Error' | 'Timeout' | null>(null);

  const recordDegradation = useCallback(
    (reason: 'Error' | 'Timeout' | 'SubmittedBeforeResolved', elapsedMs?: number) => {
      if (!enabled || patientId == null) return;
      void client
        .mutate({
          mutation: recordRetainedBeaconEligibilityDegradation,
          variables: { id: Number(patientId), reason, elapsedMs, surface },
        })
        .catch(() => undefined);
    },
    [client, enabled, patientId, surface]
  );

  const reset = useCallback(() => {
    requestRef.current += 1;
    setStatus('idle');
    setEligibility(null);
    setChoice(null);
    setDegradationReason(null);
    startedAtRef.current = null;
  }, []);

  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    []
  );

  const begin = useCallback(() => {
    reset();
    if (!enabled || patientId == null) return;

    const request = requestRef.current;
    startedAtRef.current = Date.now();
    setStatus('loading');

    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timeoutHandle = setTimeout(
        () => reject(new EligibilityTimeoutError('Retained beacon eligibility request timed out')),
        timeoutMs
      );
    });

    void Promise.race([
      client.query<{ retainedBeaconDischargeEligibility: RetainedBeaconEligibility }>({
        query: retainedBeaconDischargeEligibility,
        variables: { id: Number(patientId) },
        fetchPolicy: 'network-only',
      }),
      timeout,
    ])
      .then(result => {
        if (request !== requestRef.current) return;
        const next = result.data.retainedBeaconDischargeEligibility;
        setEligibility(next);
        setStatus(next.eligible ? 'eligible' : 'ineligible');
      })
      .catch(error => {
        if (request !== requestRef.current) return;
        const reason = error instanceof EligibilityTimeoutError ? 'Timeout' : 'Error';
        const elapsedMs = elapsedSince(startedAtRef.current);
        setDegradationReason(reason);
        console.warn('[RetainedBeacon] EligibilityDegraded', {
          patientId,
          reason,
          elapsedMs,
          surface,
          error: error instanceof Error ? error.message : String(error),
        });
        recordDegradation(reason, elapsedMs);
        setStatus('ineligible');
      })
      .finally(() => {
        if (timeoutHandle) clearTimeout(timeoutHandle);
      });
  }, [client, enabled, patientId, recordDegradation, reset, surface, timeoutMs]);

  const prepareSubmission = useCallback(
    (selectedChoice: RetainedBeaconChoice | null = choice) => {
      if (status === 'loading') {
        requestRef.current += 1;
        setStatus('ineligible');
        const elapsedMs = elapsedSince(startedAtRef.current);
        console.info('[RetainedBeacon] SubmittedBeforeResolved', {
          patientId,
          elapsedMs,
          surface,
        });
        recordDegradation('SubmittedBeforeResolved', elapsedMs);
        return false;
      }

      return shouldKeepBeacon(status, selectedChoice);
    },
    [choice, patientId, recordDegradation, status, surface]
  );

  return {
    status,
    eligibility,
    degradationReason,
    choice,
    setChoice,
    begin,
    reset,
    prepareSubmission,
    requiresChoice: status === 'eligible',
    canSubmit: canSubmitRetainedBeaconDischarge(status, choice),
  };
}
